import { ImportMenuV2Dto } from '../../types/api/menu.types';
import {
  CategoryV2Response,
  MenuAvailabilityV2Response,
  MenuV2Response,
  ModifierGroupItemV2Response,
} from '../../types/menu.types';

/**
 * The AI import preview endpoint returns an `ImportMenuV2Dto`, but the shared
 * menu preview editor (`MenuImportPreview` + `MenuProviderSectionsPreview/*`)
 * is driven by a `MenuV2Response`. This converts one into the other so the AI
 * result reuses the exact same editor + commit path.
 *
 * The ids we assign to categories/items are synthetic but internally
 * consistent — that's all `handleSubmitImportMenu` needs to rebuild the DTO on
 * commit (it reads `category.id`, `item.modifierGroups[].id`, and
 * `category.schedules[].id`). Modifier-group and schedule ids are preserved as
 * given so `modifierGroupIds` / `scheduleIds` references resolve correctly.
 */
export const importMenuV2DtoToMenuV2Response = (
  dto: ImportMenuV2Dto,
  allowedToEditMenuPrice = true
): MenuV2Response => {
  // schedules -> availability (same { id, name, hours } shape)
  const availability: MenuAvailabilityV2Response[] = dto.schedules.map(
    (schedule) => ({
      id: schedule.id,
      name: schedule.name,
      hours: schedule.hours,
    })
  );
  const availabilityById = new Map(availability.map((a) => [a.id, a]));

  // modifier groups keep their numeric ids (referenced by item.modifierGroupIds)
  const modifierGroups: ModifierGroupItemV2Response[] = dto.modifierGroups.map(
    (group) => ({
      id: group.id,
      name: group.name,
      description: group.description,
      type: group.type,
      required: group.required,
      selectionsMin: group.selectionsMin,
      selectionsMax: group.selectionsMax,
      options: group.options.map((option, index) => ({
        id: `${group.id}-${index}`,
        name: option.name,
        description: option.description ?? null,
        price: option.price,
        availability: option.availability,
        modifierGroups: [],
        customizedPrices: option.customizedPrices,
      })),
    })
  );
  const modifierGroupById = new Map(modifierGroups.map((g) => [g.id, g]));

  let itemId = 0;

  const categories: CategoryV2Response[] = dto.categories.map(
    (category, categoryIndex) => ({
      id: categoryIndex + 1,
      name: category.name,
      description: category.description,
      schedules: category.scheduleIds
        .map((id) => availabilityById.get(id))
        .filter((s): s is MenuAvailabilityV2Response => Boolean(s)),
      items: category.items.map((item, index) => {
        const { menuItem } = item;
        return {
          id: (itemId += 1),
          name: menuItem.name,
          description: menuItem.description,
          price: menuItem.price,
          pickupPrice: menuItem.pickupPrice,
          customizedPrices: menuItem.customizedPrices,
          imageUrl: item.imageUrl || '',
          availability: menuItem.availability,
          preparationNotes: menuItem.preparationNotes ?? null,
          order: index,
          pausedUntilEndOfTheDay: false,
          allowedToEditMenuPrice,
          modifierGroups: menuItem.modifierGroupIds
            .map((id) => modifierGroupById.get(id))
            .filter((g): g is ModifierGroupItemV2Response => Boolean(g)),
        };
      }),
    })
  );

  return { categories, modifierGroups, availability };
};
